import { useState, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import { getStorage, ref, uploadString } from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const storage = getStorage(app);

function App() {
  const [formData, setFormData] = useState({
    // Teilnehmer
    teilnehmerVorname: '',
    teilnehmerNachname: '',
    geburtsdatum: '',
    
    // Erziehungsberechtigte
    elternVorname: '',
    elternNachname: '',
    titel: '',
    strasse: '',
    hausNr: '',
    plz: '',
    ort: '',
    telefon: '',
    mobil: '',
    email: '',
    
    // Bankverbindung
    bank: '',
    bankSitz: '',
    bic: '',
    iban: '',
    kontoInhaber: '',
    referenzNr: '',
    
    // Kursdetails
    kursTyp: '',
    kursTage: [],
    startDatum: '',
    starterSet: true,
    
    // Zustimmungen
    zustimmungZahlungen: false,
    zustimmungTeilnahme: false,
    zustimmungDatenschutz: false,

    genehmigt: false,
    archiviert: false,
    
    timestamp: new Date()
  });
  
  const [isSuccess, setIsSuccess] = useState(false);
  const [signature, setSignature] = useState(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastX, setLastX] = useState(0);
  const [lastY, setLastY] = useState(0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (formData.kursTage.length === 0) {
      alert("Bitte wählen Sie mindestens einen Kurstag aus.");
      return false;
    }
    return true;
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    setIsDrawing(true);
    
    // Skalierungsfaktor berechnen
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // Position unter Berücksichtigung der Skalierung
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * scaleX;
    const y = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * scaleY;
    
    setLastX(x);
    setLastY(y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault(); // Verhindert unerwünschtes Scrollen auf Touch-Geräten
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Skalierungsfaktor berechnen
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // Aktuelle Position unter Berücksichtigung der Skalierung
    const currentX = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * scaleX;
    const currentY = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * scaleY;
    
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(currentX, currentY);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round'; // Macht die Linien runder
    ctx.lineJoin = 'round'; // Macht die Verbindungen runder
    ctx.stroke();
    
    setLastX(currentX);
    setLastY(currentY);
  };

  const endDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL();
    setSignature(signatureData);
  };

  // Überprüft, ob die Signatur leer ist
const isSignatureEmpty = (signatureDataUrl) => {
  // Erstelle ein leeres Canvas
  const emptyCanvas = document.createElement('canvas');
  emptyCanvas.width = 800; // Setze die gleiche Größe wie das tatsächliche Canvas
  emptyCanvas.height = 300; // (Anpassen an dein tatsächliches Canvas)

  const emptyCtx = emptyCanvas.getContext('2d');
  emptyCtx.fillStyle = 'white';
  emptyCtx.fillRect(0, 0, emptyCanvas.width, emptyCanvas.height);

  // Vergleiche die Daten-URL des leeren Canvas mit der Signatur
  const emptyDataUrl = emptyCanvas.toDataURL();
  return signatureDataUrl === emptyDataUrl;
};

const clearSignature = () => {
  const canvas = canvasRef.current;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  setSignature(null);
};
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
    // Validierung: Ist die Signatur vorhanden und nicht leer?
    if (!signature || isSignatureEmpty(signature)) {
      alert("Bitte unterschreiben Sie das Formular.");
      return;
    }
  
    try {
      // Konvertiere die Daten in das richtige Format
      const dataToSubmit = {
        ...formData,
        signatureUrl: signature,
        timestamp: new Date().toISOString(), // Timestamp als String speichern
        kursTage: formData.kursTage || [], // Sicherstellen, dass kursTage ein Array ist
        starterSet: Boolean(formData.starterSet), // Explizit als Boolean speichern
        zustimmungZahlungen: Boolean(formData.zustimmungZahlungen),
        zustimmungTeilnahme: Boolean(formData.zustimmungTeilnahme),
        zustimmungDatenschutz: Boolean(formData.zustimmungDatenschutz),
      };  

      // Zu Firebase hinzufügen
      const docRef = await addDoc(collection(db, "users"), dataToSubmit);
      console.log("Dokument wurde hinzugefügt mit ID: ", docRef.id);
      
      // Erfolgsmeldung anzeigen
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 3000);
      
      // Formular zurücksetzen
      setFormData({
        teilnehmerVorname: '',
        teilnehmerNachname: '',
        geburtsdatum: '',
        elternVorname: '',
        elternNachname: '',
        titel: '',
        strasse: '',
        hausNr: '',
        plz: '',
        ort: '',
        telefon: '',
        mobil: '',
        email: '',
        bank: '',
        bankSitz: '',
        bic: '',
        iban: '',
        kontoInhaber: '',
        kursTyp: '',
        kursTage: [],
        startDatum: '',
        starterSet: true,
        zustimmungZahlungen: false,
        zustimmungTeilnahme: false,
        zustimmungDatenschutz: false,
        timestamp: new Date(),
        genehmigt: false,
        referenzNr: '',
        archiviert: false,
      });
      clearSignature()
      
    } catch (error) {
      console.error("Fehler beim Speichern: ", error);
      alert("Es gab einen Fehler beim Speichern der Anmeldung. Bitte versuchen Sie es erneut.");
    }
    }
  };

  return (
    <div style={{
      padding: '40px', 
      maxWidth: '1200px',
      margin: '0 auto', 
      fontFamily: 'Arial, sans-serif'
    }}>
      <h1 style={{ textAlign: 'center', marginBottom: '30px' }}>
        Anmeldung für den Unterricht
      </h1>
      <h2 style={{ textAlign: 'center', marginBottom: '30px' }}>
        Kunstschule Neuss<br />
        Horremer Straße 7, 41470 Neuss
      </h2>

      <form onSubmit={handleSubmit} style={{display: 'grid', gap: '20px'}}>
        {/* Teilnehmer Sektion */}
        <fieldset style={{ 
          border: 'none', 
          padding: '15px 0',  // Gleicher Padding wie andere Sektionen
          marginBottom: '20px',
          backgroundColor: 'white',
          borderRadius: '8px'
        }}>
          <legend>Persönliche Daten Teilnehmer/in</legend>
          <div style={containerStyle}>
            <input
              name="teilnehmerVorname"
              value={formData.teilnehmerVorname}
              onChange={handleInputChange}
              placeholder="Vorname"
              required
              style={inputStyle}
            />
            <input
              name="teilnehmerNachname"
              value={formData.teilnehmerNachname}
              onChange={handleInputChange}
              placeholder="Nachname"
              required
              style={inputStyle}
            />
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
                Geburtstag des Teilnehmers:
              </label>
              <input
                type="date"
                name="geburtsdatum"
                value={formData.geburtsdatum}
                onChange={handleInputChange}
                required
                style={inputStyle}
              />
            </div>
          </div>
        </fieldset>

        {/* Erziehungsberechtigte Sektion */}
        {formData.kursTyp !== 'erwachsene' && (
          <fieldset style={{ 
            border: 'none', 
            padding: '15px 0',  
            marginBottom: '20px',
            backgroundColor: 'white',
            borderRadius: '8px'
          }}>
            <legend>Persönliche Daten Erziehungsberechtigte/r (Nur bei Schülern unter 18 J.)</legend>
            <div style={containerStyle}>
              {/* Gruppe für Namen mit Abstand nach unten */}
              <div style={{ gridColumn: '1 / -1', marginBottom: '50px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <input
                    name="elternVorname"
                    value={formData.elternVorname}
                    onChange={handleInputChange}
                    placeholder="Vorname"
                    style={inputStyle}
                  />
                  <input
                    name="elternNachname"
                    value={formData.elternNachname}
                    onChange={handleInputChange}
                    placeholder="Nachname"
                    style={inputStyle}
                  />
                  <input
                    name="titel"
                    value={formData.titel}
                    onChange={handleInputChange}
                    placeholder="Titel"
                    style={inputStyle}
                  />
                </div>
              </div>
              <legend>Weitere Daten</legend>
              {/* Rest der Eingabefelder */}
              <div style={{ display: 'flex', gap: '10px', gridColumn: '1 / -1' }}>
                <input
                  name="strasse"
                  value={formData.strasse}
                  onChange={handleInputChange}
                  placeholder="Straße"
                  required
                  style={{ ...inputStyle, flex: 3 }}
                />
                <input
                  name="hausNr"
                  value={formData.hausNr}
                  onChange={handleInputChange}
                  placeholder="Haus-Nr"
                  required
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
              <input
                name="plz"
                value={formData.plz}
                onChange={handleInputChange}
                placeholder="PLZ"
                required
                style={inputStyle}
              />
              <input
                name="ort"
                value={formData.ort}
                onChange={handleInputChange}
                placeholder="Ort"
                required
                style={inputStyle}
              />
              <input
                name="mobil"
                value={formData.mobil}
                onChange={handleInputChange}
                placeholder="Mobil"
                type="tel"
                required
                style={inputStyle}
              />
              <input
                name="telefon"
                value={formData.telefon}
                onChange={handleInputChange}
                placeholder="Telefon (optional)"
                type="tel"
                style={inputStyle}
              />
              <input
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="E-Mail"
                type="email"
                required
                style={{ ...inputStyle, gridColumn: '1 / -1' }}
              />
            </div>
          </fieldset>
        )}

        {/* Adressfelder für Erwachsene */}
        {formData.kursTyp === 'erwachsene' && (
          <fieldset style={{ 
            border: 'none', 
            padding: '15px 0',  
            marginBottom: '20px',
            backgroundColor: 'white',
            borderRadius: '8px'
          }}>
            <legend>Kontaktdaten</legend>
            <div style={containerStyle}>
              <div style={{ display: 'flex', gap: '10px', gridColumn: '1 / -1' }}>
                <input
                  name="strasse"
                  value={formData.strasse}
                  onChange={handleInputChange}
                  placeholder="Straße"
                  required
                  style={{ ...inputStyle, flex: 3 }}
                />
                <input
                  name="hausNr"
                  value={formData.hausNr}
                  onChange={handleInputChange}
                  placeholder="Haus-Nr"
                  required
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
              <input
                name="plz"
                value={formData.plz}
                onChange={handleInputChange}
                placeholder="PLZ"
                required
                style={inputStyle}
              />
              <input
                name="ort"
                value={formData.ort}
                onChange={handleInputChange}
                placeholder="Ort"
                required
                style={inputStyle}
              />
            </div>
          </fieldset>
        )}

        {/* Bankverbindung Sektion */}
        <fieldset style={{ 
          border: 'none', 
          padding: '15px 0', 
          marginBottom: '20px',
          backgroundColor: 'white'  // Weißer Hintergrund für jeden Abschnitt
        }}>
          <legend>Bankverbindung</legend>
          <div style={containerStyle}>
            <input
              name="bank"
              value={formData.bank}
              onChange={handleInputChange}
              placeholder="Bank"
              required
              style={inputStyle}
            />
            <input
              name="bankSitz"
              value={formData.bankSitz}
              onChange={handleInputChange}
              placeholder="Sitz der Bank"
              required
              style={inputStyle}
            />
            <input
              name="bic"
              value={formData.bic}
              onChange={handleInputChange}
              placeholder="BIC / BLZ"
              required
              style={inputStyle}
            />
            <input
              name="iban"
              value={formData.iban}
              onChange={handleInputChange}
              placeholder="IBAN / Konto-Nr."
              required
              style={inputStyle}
            />
            <input
              name="kontoInhaber"
              value={formData.kontoInhaber}
              onChange={handleInputChange}
              placeholder="Konto-Inhaber"
              required
              style={inputStyle}
            />
          </div>
        </fieldset>

        {/* Zustimmung Zahlungen */}
        <div style={{ 
          backgroundColor: 'white',
          padding: '15px 0',  // Kein seitliches Padding
          marginBottom: '20px'
        }}>
          <legend>Zustimmungen</legend>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'start', 
              gap: '10px',
              backgroundColor: '#f8f8f8',
              padding: '12px',
              borderRadius: '6px'
            }}>
              <input
                type="checkbox"
                required
                checked={formData.zustimmungZahlungen}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  zustimmungZahlungen: e.target.checked
                }))}
              />
              <span style={{ fontSize: '14px', lineHeight: '1.5' }}>
              Ich ermächtige (Wir ermächtigen) Peter Dück, Zahlungen / Monatsgebühren von meinem (unserem) Konto mittels Lastschrift einzuziehen. Zugleich weise ich mein (weisen wir unser) Kreditinstitut an, die von Peter Dück auf mein (unser) Konto gezogenen Lastschriften einzulösen. Hinweis: Ich kann (Wir können) innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrages verlangen. Es gelten dabei die mit meinem (unserem) Kreditinstitut vereinbarten Bedingungen.
              </span>
            </label>

            <label style={{ 
              display: 'flex', 
              alignItems: 'start', 
              gap: '10px',
              backgroundColor: '#f8f8f8',
              padding: '12px',
              borderRadius: '6px'
            }}>
              <input
                type="checkbox"
                required
                checked={formData.zustimmungTeilnahme}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  zustimmungTeilnahme: e.target.checked
                }))}
              />
              <span style={{ fontSize: '14px', lineHeight: '1.5' }}>
              Die Unterrichtskosten siehe Angaben unten. Die Monatsgebühren werden 1-3 Tagen vor Unterrichtsbeginn und Folgemonaten vom Konto abgebucht. Die Anmeldung verlängert sich automatisch um 1 Monat für den ausgewählten Kurs, sofern nicht bis Ende des laufenden Monats eine Abmeldung eingeht.
              </span>
            </label>

            <label style={{ 
              display: 'flex', 
              alignItems: 'start', 
              gap: '10px',
              backgroundColor: '#f8f8f8',
              padding: '12px',
              borderRadius: '6px'
            }}>
              <input
                type="checkbox"
                required
                checked={formData.zustimmungDatenschutz}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  zustimmungDatenschutz: e.target.checked
                }))}
              />
              <span style={{ fontSize: '14px', lineHeight: '1.5' }}>
              Ich habe die Teilnahmebedingungen und die Datenschutzerklärung der Kunstschule Neuss gelesen, erkenne diese an und melde mein Kind / mich an.
              </span>
            </label>
          </div>
        </div>

{/* Kursauswahl */}
<fieldset style={{
  border: 'none',
  padding: '15px 0',
  marginBottom: '20px',
  backgroundColor: 'white',
  borderRadius: '8px'
}}>
  <legend>Kursauswahl</legend>
  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
    {/* Kurstyp Auswahl */}
    <select
      name="kursTyp"
      value={formData.kursTyp}
      onChange={handleInputChange}
      required
      style={{
        ...inputStyle,
        backgroundColor: 'white',
        height: '48px',
        cursor: 'pointer',
        appearance: 'none',
        backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23007AFF%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.4-12.8z%22%2F%3E%3C%2Fsvg%3E")',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px top 50%',
        backgroundSize: '12px auto',
        paddingRight: '30px'
      }}
    >
      <option value="">Bitte Kurs wählen</option>
      <option value="kinder5-9">Kinder 5-9 Jahre</option>
      <option value="kinder9plus">Kinder ab 9 Jahre und Jugendliche</option>
      <option value="erwachsene">Erwachsene</option>
    </select>

    {/* Kurstage und Kursdetails */}
    <div style={{
      backgroundColor: '#f8f8f8',
      padding: '15px',
      borderRadius: '8px'
    }}>
      <label style={{
        display: 'block',
        marginBottom: '10px',
        fontWeight: '500'
      }}>
        Kurstage:
      </label>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '15px',
        marginBottom: '20px'
      }}>
        {formData.kursTyp === 'erwachsene'
          ? ['Di', 'Mi'].map(tag => (
            <label key={tag} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '80px',
              backgroundColor: 'white',
              padding: '8px 12px',
              borderRadius: '6px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                name="kursTage"
                value={tag}
                checked={formData.kursTage.includes(tag)}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    kursTage: e.target.checked
                      ? [...prev.kursTage, value]
                      : prev.kursTage.filter(t => t !== value)
                  }));
                }}
              /> {tag}
            </label>
          ))
          : ['Mo', 'Di', 'Mi', 'Do', 'Fr'].map(tag => (
            <label key={tag} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '80px',
              backgroundColor: 'white',
              padding: '8px 12px',
              borderRadius: '6px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                name="kursTage"
                value={tag}
                checked={formData.kursTage.includes(tag)}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    kursTage: e.target.checked
                      ? [...prev.kursTage, value]
                      : prev.kursTage.filter(t => t !== value)
                  }));
                }}
              /> {tag}
            </label>
          ))}
      </div>

      {formData.kursTyp && (
        <div style={{
          backgroundColor: 'white',
          padding: '15px',
          borderRadius: '6px',
          fontSize: '14px',
          lineHeight: '1.5'
        }}>
          {formData.kursTyp === 'kinder5-9' && (
            <p>Kurszeit: 16:00 - 16:45 Uhr<br />Kosten: 56,- EUR/Monat</p>
          )}
          {formData.kursTyp === 'kinder9plus' && (
            <p>Kurszeit: 17:00 - 18:45 Uhr<br />Kosten: 92,- EUR/Monat</p>
          )}
          {formData.kursTyp === 'erwachsene' && (
            <p>Kurszeit: 19:15 - 21:00 Uhr<br />Kosten: 13,50 EUR/Unterrichtsstunde</p>
          )}
        </div>
      )}
    </div>


            {/* Startdatum */}
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                Beginn der Teilnahme:
              </label>
              <input
                type="date"
                name="startDatum"
                value={formData.startDatum}
                onChange={handleInputChange}
                required
                style={inputStyle}
              />
            </div>
          </div>
        </fieldset>

        {/* Starter Set */}
        <div style={{ border: '1px solid #ccc', padding: '20px', borderRadius: '8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              checked={formData.starterSet}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                starterSet: e.target.checked
              }))}
            />
            <span>Starter-Set (69,- EUR)</span>
          </label>
        </div>

        {/* Unterschrift */}
        <fieldset style={{ 
          border: 'none', 
          padding: '15px 0', 
          marginBottom: '20px',
          backgroundColor: 'white',
          borderRadius: '8px'
        }}>
          <legend>Unterschrift</legend>
          <div style={{ 
            backgroundColor: '#f8f8f8',
            padding: '15px',
            borderRadius: '8px'
          }}>
            <canvas
              ref={canvasRef}
              width={800}
              height={300}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={endDrawing}
              onMouseOut={endDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={endDrawing}
              style={{
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: 'white',
                touchAction: 'none',
                width: '100%',
                height: '150px',
                cursor: 'crosshair'
              }}
            />
            <button
              type="button"
              onClick={clearSignature}
              style={{
                marginTop: '10px',
                padding: '8px 16px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Unterschrift löschen
            </button>
          </div>
        </fieldset>

        {/* Kontaktinformationen */}
        <div style={{ 
          fontSize: '12px', 
          color: '#666', 
          textAlign: 'center',
          marginTop: '20px',
          padding: '20px',
          backgroundColor: '#f8f8f8'
        }}>
          Peter Dück Kunstschule Neuss · Horremer Straße 7 · 41470 Neuss · Tel.: 02137-916 68 99 · info@kunstschule-neuss.de · www.kunstschule-neuss.de<br />
        </div>

        <button 
          type="submit"
          style={{
            backgroundColor: '#007AFF',
            color: 'white',
            padding: '16px 32px',
            border: 'none',
            borderRadius: '12px',
            fontSize: '18px',
            fontWeight: '600',
            width: '100%',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginTop: '30px',
            marginBottom: '40px',
            boxShadow: '0 2px 8px rgba(0, 122, 255, 0.3)',
            ':hover': {
              backgroundColor: '#0066CC',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0, 122, 255, 0.4)'
            },
            ':active': {
              transform: 'translateY(0)',
              boxShadow: '0 2px 4px rgba(0, 122, 255, 0.2)'
            }
          }}
        >
          Anmeldung absenden
        </button>
      </form>

      {isSuccess && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#4CAF50',
          color: 'white',
          padding: '30px',
          borderRadius: '10px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
          zIndex: 1000,
          maxWidth: '80%'
        }}>
          <h2 style={{ marginBottom: '15px' }}>✓ Anmeldung erfolgreich!</h2>
          <p style={{ fontSize: '16px', marginBottom: '10px' }}>
            Ihre Anmeldung wurde erfolgreich übermittelt und wird von uns bearbeitet.
          </p>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  padding: '12px',
  fontSize: '16px',
  borderRadius: '6px',
  border: '1px solid #ccc',
  width: '100%',
  boxSizing: 'border-box'
};

const containerStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '15px',
  width: '100%'
};

export default App;
